import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Injectable, PLATFORM_ID, REQUEST, Renderer2, RendererFactory2, inject } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, first, map } from 'rxjs/operators';
import {
  BurgerMenuContent,
  CategoryBurgerLink,
  ConfirmationAdvertisement,
  Content,
  ContentProperty,
  ContentResponse,
  ContentSection,
  ErrorMessages,
  EventStore,
  LinkContent,
  MediaContent,
  Merchant,
  MerchantContent,
  NewsletterSubscription,
  OnlineContent,
  OnlineContentTypes,
  OnlineIconLine,
  OnlineInfobox,
  OnlineStrip,
  OnlineStripContent,
  OnlineStripImage,
  OnlineStripInfobox,
  OnlineStripInfoboxes,
  PageContent,
  ParsedContentResponse,
  PropertiesToMap,
  Question,
  Smiley,
  StoreType,
  SubNavigationContent,
  UmbracoLink,
} from 'src/app/models';
import { environment } from 'src/environments/environment';
import { ThemeService } from '../theme/theme.service';
import { EventStoreService } from './event-store.service';

export const sections = {
  header: 'nxtHeader',
  navigationHeader: 'nxtNavigationHeader',
  footer: 'nxtFooter',
  richFooter: 'nxtFooterRTE',
  seo: 'nxtSeo',
  sharedContent: 'sharedContent',
  merchantContentFolder: 'merchantContentFolder',
  confirmationAdvertisementFolder: 'confirmationAdvertisementFolder',
  onlineFolder: 'onlineFolder',
  errors: 'errors',
  infoPopup: 'infoPopup',
};

export const headerSections = {
  header: 'headerSection',
  navigation: 'navigationSection',
};

const STORE_CLOSED = 'Closed';

@Injectable({ providedIn: 'root' })
export class ContentService {
  private http = inject(HttpClient);
  private titleService = inject(Title);
  private metaService = inject(Meta);
  private rendererFactory = inject(RendererFactory2);
  private themeService = inject(ThemeService);
  private router = inject(Router);
  private platformId = inject(PLATFORM_ID);
  private request = inject(REQUEST, { optional: true });
  private eventStoreService = inject(EventStoreService);

  private readonly contentEndpoint = `${environment.apiOrigin}/Content`;
  private readonly cmsOrigin = environment.cmsOrigin;
  private readonly contentUrl = `${environment.urls.content.merchant_content}/`;
  private readonly mailingListEndpoint = `${environment.cmsOrigin}/umbraco/api/mail/GetNewsletterMailingLists`;
  private readonly smileyEndpoint = `${environment.apiOrigin}/Smiley/Fetch`;

  private renderer: Renderer2;

  private merchantId: number;
  private merchant: Merchant;
  private storeClosed: boolean;
  private content: Content = {};
  private defaultContent: Content = {};
  private contentPages: Record<string, MerchantContent[]>;
  private confirmationAdvertisements: ConfirmationAdvertisement[];
  private onlineProductContent: Record<string, OnlineContent[]>;
  private onlineCategoryContent: Record<string, OnlineStripContent[]>;
  private onlinePageContent: Record<string, OnlineStripContent[]>;
  private onlineHomeContent: OnlineStripContent[];
  private navigationHeader: boolean;
  public requestUrl: string;
  public richFooter: boolean;
  public errorMessages: ErrorMessages;
  public mailingWorkspace: string;
  public mailingLists: BehaviorSubject<NewsletterSubscription[]>;
  public noContent = false;

  public loaded: BehaviorSubject<boolean>;

  constructor() {
    if (typeof window !== 'undefined') {
      this.requestUrl = window.location.host;
    } else {
      this.requestUrl = this.request?.headers.get('host') || environment.buildOrigin;
    }

    if (!environment.production && environment.devOrigin) {
      const url = environment.devOrigin.replace(/http(s)?:\/\//, '').replace('/', '');
      this.requestUrl = url;
    }

    this.renderer = this.rendererFactory.createRenderer(null, null);

    this.mailingLists = new BehaviorSubject<NewsletterSubscription[]>(undefined);

    this.loaded = new BehaviorSubject(false);
  }

  getMerchantId(): number {
    return this.merchantId;
  }

  getMerchant(): Merchant {
    return this.merchant;
  }

  getStoreAddress(): string {
    if (!this.has(sections.header)) {
      return undefined;
    }

    return this.get(sections.header).get('address');
  }

  getRequestUrl(): string {
    return this.requestUrl;
  }

  getTitle(): string {
    if (!this.has(sections.seo)) {
      return undefined;
    }

    return this.get(sections.seo).get('websiteTitle');
  }

  getDescription(): string {
    if (!this.has(sections.seo)) {
      return undefined;
    }

    return this.get(sections.seo).get('websiteDescription');
  }

  getFavicon(): string {
    if (!this.has(sections.seo)) {
      return undefined;
    }

    return this.get(sections.seo).get('favicon');
  }

  getOGImage(): string {
    if (!this.has(sections.seo)) {
      return undefined;
    }

    return this.get(sections.seo).get('ogImage');
  }

  getDisclaimer(): string {
    if (!this.has(sections.header)) {
      return undefined;
    }

    return this.get(sections.header).get('disclaimerContentText');
  }

  getMerchantPages(): Map<string, PageContent> {
    let nav = this.get(headerSections.navigation);
    if (!nav) {
      nav = new Map();
    }

    let header = this.get(headerSections.header);
    if (!header) {
      header = new Map();
    }

    return new Map([...nav, ...header]);
  }

  getMerchantContent(section = headerSections.navigation): MerchantContent[] {
    if (!this.has(section)) {
      return [];
    }

    return this.contentPages[section];
  }

  getConfirmationAds(): ConfirmationAdvertisement[] {
    return this.confirmationAdvertisements;
  }

  getOnlineProductContent<T extends OnlineContent>(productId: number, type: OnlineContentTypes): T[] {
    if (!this.onlineProductContent) return [];
    const entry = (productId && this.onlineProductContent[productId]) || [];
    const content = entry.filter((c) => c.type === type) as any;
    return content;
  }

  getOnlineProductStripContent<T extends OnlineContent>(productId: number): T[] {
    if (!this.onlineProductContent) return [];
    const entry = (productId && this.onlineProductContent[productId]) || [];
    const content = entry.filter(
      (c) =>
        c.type === OnlineContentTypes.Strip || c.type === OnlineContentTypes.StripImage || c.type === OnlineContentTypes.StripInfoboxes,
    ) as any;
    return content;
  }

  getOnlineCategoryContent(categoryId: number) {
    if (!this.onlineCategoryContent) return [];
    const content = (categoryId && this.onlineCategoryContent[categoryId]) || [];
    return content;
  }

  getOnlinePageContent(contentId: string) {
    if (!this.onlinePageContent) return [];
    const content = (contentId && this.onlinePageContent[contentId]) || [];
    return content;
  }

  getOnlineHomeContent() {
    const content = this.onlineHomeContent;
    return content;
  }

  isNavigationHeader(): boolean {
    return this.navigationHeader;
  }

  getSharedContent(): Map<string, string> {
    return this.get(sections.sharedContent);
  }

  getStoreClosed() {
    return this.storeClosed;
  }

  getInfoPopup(): Map<string, PageContent> {
    if (!this.has(sections.infoPopup)) {
      return undefined;
    }

    return this.get(sections.infoPopup);
  }

  get isEventStore(): boolean {
    return this.eventStoreService.isEventStore;
  }

  get eventStores(): EventStore[] {
    return this.eventStoreService.eventStores();
  }

  get isDagrofa() {
    const { type } = this.merchant;
    return type === StoreType.MENY || type === StoreType.SPAR || type === StoreType.MIN_KOBMAND;
  }

  get(key: string): Map<string, any> {
    if (this.content[key]) return this.content[key];
    if (this.defaultContent[key]) return this.defaultContent[key];
    return new Map();
  }

  getDefault(key: string): Map<string, any> {
    if (this.defaultContent[key]) return this.defaultContent[key];
    return new Map();
  }

  has(key: string): boolean {
    return !!this.content[key] || !!this.defaultContent[key];
  }

  addImageSource(contentText: string): string {
    const srcRoot = 'src="/';
    const re = new RegExp(srcRoot, 'g');
    return contentText ? contentText.replace(re, `src="${this.cmsOrigin}/`) : contentText;
  }

  addLinkSource(contentText: string): string {
    const srcRoot = 'href="/media';
    const re = new RegExp(srcRoot, 'g');
    return contentText ? contentText.replace(re, `href="${this.cmsOrigin}/media`) : contentText;
  }

  processLink(e: any) {
    const element: HTMLElement = e.target;
    if (element.nodeName === 'A') {
      const a = element as HTMLAnchorElement;
      if (a.host !== window.location.host) {
        return;
      }

      e.preventDefault();
      const link = decodeURIComponent(a.pathname);
      this.router.navigate([link]);
    }
  }

  setTitle(title?: string) {
    if (!title) {
      this.titleService.setTitle(this.getTitle());
      return;
    }

    this.titleService.setTitle(`${this.getTitle()} | ${title}`);
  }

  setDescription(description?: string) {
    const update = this.metaService.getTag('name=description');

    if (!description) {
      if (update) {
        this.metaService.updateTag({ name: 'description', content: this.getDescription() });
      } else {
        this.metaService.addTag({ name: 'description', content: this.getDescription() });
      }

      return;
    }

    if (update) {
      this.metaService.updateTag({ name: 'description', content: description });
    } else {
      this.metaService.addTag({ name: 'description', content: description });
    }
  }

  setOGImage(imgUrl?: string) {
    imgUrl = imgUrl ? imgUrl : this.getOGImage();
    if (!imgUrl) {
      this.metaService.removeTag('name="image"');
      this.metaService.removeTag('name="image:width"');
      return;
    }

    this.metaService.updateTag({ name: 'image', property: 'og:image', content: `${this.cmsOrigin}${imgUrl}?width=500&mode=max` });
    this.metaService.updateTag({ name: 'image:width', property: 'og:image:width', content: '500' });
  }

  setNoIndex(noIndex: boolean) {
    const robots = this.metaService.getTag('name=robots');
    if (!robots && !noIndex) {
      return;
    }

    if (noIndex && robots) {
      return;
    }

    if (!noIndex && robots) {
      this.metaService.removeTag('name=robots');
      return;
    }

    this.metaService.addTag({ name: 'robots', content: 'noindex' });
  }

  getContentLink(page: PageContent | LinkContent): LinkContent {
    if (page.type === 'link') {
      return page;
    }

    const link: LinkContent = {
      type: 'link',
      titleText: page.titleText,
      targetUrl: page.pageUrl,
      new: false,
    };
    return link;
  }

  sameSite<T extends LinkContent | UmbracoLink>(l: T): T {
    const link = JSON.parse(JSON.stringify(l));

    if ('targetUrl' in link) {
      link.targetUrl = this.checkSameSite(link.targetUrl);
      if (link.targetUrl) return link;
    }

    if ('Url' in link) {
      link.Url = this.checkSameSite(link.Url);
      if (link.Url) return link;
    }

    return undefined;
  }

  checkSameSite(url: string): string {
    const a = this.renderer.createElement('a');
    a.href = url;

    if (isPlatformBrowser(this.platformId) && a.host !== window.location.host) {
      return undefined;
    }
    if (isPlatformServer(this.platformId) && a.host !== this.request.headers.get('host')) {
      return undefined;
    }
    if (url.startsWith('/media/')) {
      return undefined;
    }

    url = decodeURIComponent(a.pathname);
    return url;
  }

  getSmileys(nameTrackingIds: number[]): Observable<Smiley[]> {
    return this.http.post<Smiley[]>(this.smileyEndpoint, { nameTrackingIds });
  }

  load(): Promise<any> {
    return new Promise<ParsedContentResponse>((resolve) => {
      const content = this.contentRequests();

      content.subscribe(async (contentResponse) => {
        this.merchantId = contentResponse.merchant.id;
        this.merchant = contentResponse.merchant;
        if (!(contentResponse.content.Children?.length > 0)) {
          this.noContent = true;
          this.contentPages = {};
          return resolve(contentResponse);
        }

        this.storeClosed = contentResponse.content.Children[0].Properties[0].Value === STORE_CLOSED;

        const contentBase = contentResponse.content.Children[0];
        this.content = this.contentSectionsToPropMap(contentBase.Children);

        const defaultContentBase = contentResponse.defaultContent.Children[0];
        this.defaultContent = this.contentSectionsToPropMap(defaultContentBase?.Children, true);

        this.setTitle();
        this.setDescription();
        this.setOGImage();

        this.setStoreType();
        this.setMailingWorkspace();

        if (this.merchant.isEventStore) await this.eventStoreService.load(this.merchantId);

        const errorContent = this.get(sections.errors);
        this.errorMessages = {
          somethingWentWrong: errorContent.get('somethingWentWrongText'),
          invalidLogin: errorContent.get('invalidLoginText'),
        };

        this.loaded.next(true);
        resolve(contentResponse);
      });
    });
  }

  private contentRequests(): Observable<ParsedContentResponse> {
    // TODO: get default content from the same call
    const bypassCache =
      typeof window !== 'undefined'
        ? window.location.search === '?preview=true' || environment.apiOrigin === 'http://localhost:56094'
        : false;
    const content = this.http
      .get<ContentResponse>(this.contentEndpoint, {
        params: {
          callerHost: this.requestUrl,
          bypassCache,
        },
      })
      .pipe(
        catchError((err) => {
          const content: ContentResponse = {
            content: JSON.stringify({ Name: '', Children: [] }),
            defaultContent: JSON.stringify({ Name: '', Children: [] }),
            merchant: { id: 2 } as Merchant,
          };
          return of(content);
        }),
        first(),
        map<ContentResponse, ParsedContentResponse>((contentResponse) => {
          const parsedContent: ParsedContentResponse = {
            ...contentResponse,
            content: JSON.parse(contentResponse.content),
            defaultContent: JSON.parse(contentResponse.defaultContent),
          };

          return parsedContent;
        }),
      );

    return content;
  }

  private contentSectionsToPropMap(contentSections: ContentSection[], isDefault = false) {
    const content: Content = {};
    let isRichFooter = false;
    let isNavigationHeader = false;

    contentSections?.forEach((contentSection) => {
      if (contentSection.Name === sections.merchantContentFolder) {
        let section = headerSections.navigation;
        const toggle = contentSection.Properties[0].Value as any;
        if (toggle) {
          section = headerSections.header;
        }

        if (!this.contentPages) {
          this.contentPages = {};
        }

        if (isDefault && this.content[section]) return;

        const merchantContent = new Map<string, MerchantContent[]>();
        content[section] = merchantContent;

        const contentPages = contentSection.Children.map((child) => this.mapContentNodes(child, section, content)).filter(
          (entry) => !!entry,
        );
        this.contentPages[section] = contentPages;

        return;
      }

      if (contentSection.Name === sections.confirmationAdvertisementFolder) {
        const advertisements = [];
        for (const child of contentSection.Children) {
          const ad: ConfirmationAdvertisement = {
            descriptionText: child.Properties[0].Value,
            link: child.Properties[1].Value,
            background: child.Properties[2]?.Value,
            advertisementImage: child.Properties[3]?.Value,
            iconImage: child.Properties[4]?.Value,
            priority: child.Properties[5]?.Value || 0,
            productIds: child.Properties[6]?.Value,
          };

          advertisements.push(ad);
        }

        if (isDefault && this.confirmationAdvertisements) this.confirmationAdvertisements.push(...advertisements);
        else this.confirmationAdvertisements = advertisements;

        return;
      }

      if (isRichFooter && contentSection.Name === sections.footer) {
        return;
      }

      if (contentSection.Name === sections.richFooter) {
        contentSection.Name = sections.footer;
        isRichFooter = true;
      }

      if (isNavigationHeader && contentSection.Name === sections.header) {
        return;
      }

      if (contentSection.Name === sections.navigationHeader) {
        contentSection.Name = sections.header;
        isNavigationHeader = true;
      }

      if (contentSection.Name === sections.onlineFolder) {
        if (!this.onlineHomeContent) this.onlineHomeContent = [];
        if (!this.onlineCategoryContent) this.onlineCategoryContent = {};
        if (!this.onlineProductContent) this.onlineProductContent = {};
        if (!this.onlinePageContent) this.onlinePageContent = {};

        for (const content of contentSection.Children) {
          const properties = PropertiesToMap(content.Properties as ContentProperty[]);
          const productIds = properties['productIds'];

          switch (content.Name) {
            case 'onlineStrip': {
              const categoryIds = properties['categoryIds'] as string[];
              const contentPageIds = (properties['contentPageIds'] as string[]) || [];
              const strip: OnlineStrip = {
                type: OnlineContentTypes.Strip,
                productIds: productIds,
                categoryIds: categoryIds,
                contentPageIds: contentPageIds,
                displayHome: properties['displayHome'],
                stripTitleText: properties['stripTitleText'],
                link: properties['link'],
                bodyText: properties['bodyText'],
                mediaPosition: properties['mediaPosition'] ?? 'Left',
                mediaContent: properties['mediaContent'],
                mediaWidth: properties['mediaWidth'],
                backgroundColor: properties['backgroundColor'],
                primaryColor: properties['primaryColor'],
                secondaryColor: properties['secondaryColor'],
                buttonBgColor: properties['buttonBackgroundColor'],
                buttonTextColor: properties['buttonTextColor'],
              };

              // Home
              if (strip.displayHome) this.onlineHomeContent.push(strip);

              // Categories
              for (const id of categoryIds) {
                if (!(id in this.onlineCategoryContent)) this.onlineCategoryContent[id] = [];
                this.onlineCategoryContent[id].push(strip);
              }

              // Products
              for (const id of productIds) {
                if (!(id in this.onlineProductContent)) this.onlineProductContent[id] = [];
                this.onlineProductContent[id].push(strip);
              }

              // ContentPages
              for (const id of contentPageIds) {
                const url = this.contentUrl + id;
                if (!(url in this.onlinePageContent)) this.onlinePageContent[url] = [];
                this.onlinePageContent[url].push(strip);
              }

              break;
            }
            case 'onlineStripInfoboxes': {
              const infoboxes = properties['infoboxes'] as OnlineStripInfobox[];
              if (!infoboxes?.length) break;

              const categoryIds = properties['categoryIds'] as string[];
              const contentPageIds = (properties['contentPageIds'] as string[]) || [];
              const strip: OnlineStripInfoboxes = {
                type: OnlineContentTypes.StripInfoboxes,
                productIds: productIds,
                categoryIds: categoryIds,
                contentPageIds: contentPageIds,
                displayHome: properties['displayHome'],
                stripTitleText: properties['stripTitleText'],
                infoboxes: infoboxes.map((info) => {
                  let richText = this.addImageSource(info.RichText);
                  richText = this.addLinkSource(richText);
                  return {
                    image: info.Media,
                    title: info.Title,
                    text: info.Text,
                    richText,
                  };
                }),
                columns: properties['columns'],
                backgroundColor: properties['backgroundColor'],
                primaryColor: properties['primaryColor'],
                secondaryColor: properties['secondaryColor'],
              };

              // Home
              if (strip.displayHome) this.onlineHomeContent.push(strip);

              // Categories
              for (const id of categoryIds) {
                if (!(id in this.onlineCategoryContent)) this.onlineCategoryContent[id] = [];
                this.onlineCategoryContent[id].push(strip);
              }

              // Products
              for (const id of productIds) {
                if (!(id in this.onlineProductContent)) this.onlineProductContent[id] = [];
                this.onlineProductContent[id].push(strip);
              }

              // ContentPages
              for (const id of contentPageIds) {
                const url = this.contentUrl + id;
                if (!(url in this.onlinePageContent)) this.onlinePageContent[url] = [];
                this.onlinePageContent[url].push(strip);
              }

              break;
            }
            case 'onlineStripImage': {
              const categoryIds = properties['categoryIds'] as string[];
              const contentPageIds = (properties['contentPageIds'] as string[]) || [];
              const strip: OnlineStripImage = {
                type: OnlineContentTypes.StripImage,
                productIds: productIds,
                categoryIds: categoryIds,
                contentPageIds: contentPageIds,
                displayHome: properties['displayHome'],
                mediaContent: properties['mediaContent'],
                leftPadding: properties['leftPadding'],
                rightPadding: properties['rightPadding'],
                topPadding: properties['topPadding'],
                bottomPadding: properties['bottomPadding'],
                backgroundColor: properties['backgroundColor'],
              };

              // Home
              if (strip.displayHome) this.onlineHomeContent.push(strip);

              // Categories
              for (const id of categoryIds) {
                if (!(id in this.onlineCategoryContent)) this.onlineCategoryContent[id] = [];
                this.onlineCategoryContent[id].push(strip);
              }

              // Products
              for (const id of productIds) {
                if (!(id in this.onlineProductContent)) this.onlineProductContent[id] = [];
                this.onlineProductContent[id].push(strip);
              }

              // ContentPages
              for (const id of contentPageIds) {
                const url = this.contentUrl + id;
                if (!(url in this.onlinePageContent)) this.onlinePageContent[url] = [];
                this.onlinePageContent[url].push(strip);
              }

              break;
            }
            case 'onlineIconLines': {
              const lines = properties['lines'] as MediaContent[];
              const iconLine: OnlineIconLine = {
                type: OnlineContentTypes.IconLine,
                productIds: productIds,
                title: properties['titleText'],
                lines: lines.map((line) => {
                  return { icon: line.Media, text: line.Label };
                }),
              };

              // Products
              for (const id of productIds) {
                if (!(id in this.onlineProductContent)) this.onlineProductContent[id] = [];
                this.onlineProductContent[id].push(iconLine);
              }

              break;
            }
            case 'onlineInfobox': {
              const infoboxes = properties['infoboxes'] as Question[];
              if (!infoboxes?.length) break;

              const infobox: OnlineInfobox = {
                type: OnlineContentTypes.Infobox,
                productIds: productIds,
                infoboxes: infoboxes.map((i) => {
                  return { title: i.QuestionText, info: i.AnswerText };
                }),
              };

              // Products
              for (const id of productIds) {
                if (!(id in this.onlineProductContent)) this.onlineProductContent[id] = [];
                this.onlineProductContent[id].push(infobox);
              }

              break;
            }
            default:
              break;
          }
        }
      }

      const propMap = this.createPropertyMap(contentSection);
      content[contentSection.Name] = propMap;
    });

    this.richFooter = this.richFooter || isRichFooter;
    this.navigationHeader = this.navigationHeader || isNavigationHeader;

    return content;
  }

  private setStoreType() {
    const { type } = this.getMerchant();
    this.themeService.setTheme(this.renderer, type);

    this.setGoogleSiteVerification(type);
    this.setFacebookSiteVerification(type);
  }

  private setMailingWorkspace() {
    this.mailingWorkspace = this.merchant.mailingWorkspaceId;
    if (!this.mailingWorkspace) return;

    this.http
      .get<{ Data: { mailingLists: NewsletterSubscription[] } }>(this.mailingListEndpoint, {
        params: {
          workspace: this.mailingWorkspace,
        },
      })
      .pipe(first())
      .subscribe((response) => {
        this.mailingLists.next(response.Data.mailingLists);
      });
  }

  private createPropertyMap(contentSection: ContentSection): Map<string, any> {
    const propMap = new Map<string, any>();

    for (const property of contentSection.Properties) {
      propMap.set(property.Name, property.Value);
    }

    for (const child of contentSection.Children) {
      const tree = this.createPropertyNodes(child);
      propMap.set(child.Name, tree);
    }

    return propMap;
  }

  private createPropertyNodes(contentSection: ContentSection): Record<string, any> {
    const section: any = {};

    for (const property of contentSection.Properties) {
      section[property.Name] = property.Value;
    }

    for (const child of contentSection.Children) {
      const tree = this.createPropertyNodes(child);
      section[child.Name] = tree;
    }

    return section;
  }

  private mapContentNodes(node: ContentSection, section: string, content: Content): MerchantContent | undefined {
    const properties = PropertiesToMap(node.Properties);

    switch (node.Name) {
      case 'simpleContentPage': {
        const pageContent: PageContent = {
          type: 'simple',
          titleText: properties['titleText'],
          hideTitle: !!properties['hideTitleText'],
          contentText: properties['contentText'],
          mobileContentText: properties['mobileContextText'],
          pageTitle: properties['pageTitle'],
          pageDescription: properties['pageDescription'],
          pageUrl: properties['pageUrl'],
        };

        if (!pageContent.titleText) {
          return undefined;
        }

        const url = this.contentUrl + (pageContent.pageUrl.toLowerCase() || pageContent.titleText.toLowerCase());
        pageContent.pageUrl = url;
        content[section].set(url, pageContent);
        return pageContent as PageContent;
      }
      case 'hiddenSimpleContentPage': {
        const pageContent: PageContent = {
          type: 'hidden',
          titleText: properties['titleText'],
          hideTitle: !!properties['hideTitleText'],
          contentText: properties['contentText'],
          mobileContentText: properties['mobileContentText'],
          pageTitle: properties['pageTitle'],
          pageDescription: properties['pageDescription'],
          pageUrl: properties['pageUrl'],
        };

        if (!pageContent.titleText) {
          return undefined;
        }

        const url = this.contentUrl + (pageContent.pageUrl.toLowerCase() || pageContent.titleText.toLowerCase());
        pageContent.pageUrl = url;
        content[section].set(url, pageContent);
        return pageContent as PageContent;
      }
      case 'link': {
        const linkContent: LinkContent = {
          type: 'link',
          titleText: properties['titleText'],
          targetUrl: properties['targetUrl'],
          new: !!properties['new'],
        };

        if (!linkContent.titleText) {
          return undefined;
        }

        content[section].set(linkContent.titleText.toLowerCase(), linkContent);
        return linkContent;
      }
      case 'burgerMenuContent': {
        const burgerMenuContent: BurgerMenuContent = {
          type: 'burger',
          title: properties['burgerTitle'],
          pages: [],
          showContentPage: false,
          hideOnMobile: properties['mobileHide'],
        };

        if (properties['showContentPage']) {
          const subPageContent: PageContent = {
            type: 'simple',
            titleText: properties['titleText'],
            contentText: properties['contentText'],
            mobileContentText: properties['mobileContentText'],
            pageTitle: properties['pageTitle'],
            pageDescription: properties['pageDescription'],
            pageUrl: properties['pageUrl'],
          };

          if (subPageContent.titleText) {
            const url = this.contentUrl + (subPageContent.pageUrl.toLowerCase() || subPageContent.titleText.toLowerCase());
            subPageContent.pageUrl = url;
            burgerMenuContent.showContentPage = true;
            burgerMenuContent.link = url;
            burgerMenuContent.linkTarget = '_self';
            content[section].set(url, subPageContent);
          }
        } else if (properties['enableLink']) {
          burgerMenuContent.link = properties['targetUrl'];
          burgerMenuContent.linkTarget = properties['new'] ? '_blank' : '_self';
        }

        for (const child of node.Children) {
          const childPageContent = this.mapContentNodes(child, section, content) as PageContent;
          if (!childPageContent) {
            continue;
          }

          const link = this.getContentLink(childPageContent);
          burgerMenuContent.pages.push(link);
        }

        return burgerMenuContent;
      }
      case 'subNavigationContentFolder': {
        const subNavigationContent: SubNavigationContent = {
          type: 'subNav',
          title: properties['subNavigationTitle'],
          pages: [],
          showContentPage: false,
        };

        if (properties['showContentPage']) {
          const subPageContent: PageContent = {
            type: 'simple',
            titleText: properties['titleText'],
            contentText: properties['contentText'],
            mobileContentText: properties['mobileContentText'],
            pageTitle: properties['pageTitle'],
            pageDescription: properties['pageDescription'],
            pageUrl: properties['pageUrl'],
          };

          if (subPageContent.titleText) {
            const url = this.contentUrl + (subPageContent.pageUrl.toLowerCase() || subPageContent.titleText.toLowerCase());
            subPageContent.pageUrl = url;
            subNavigationContent.showContentPage = true;
            subNavigationContent.link = url;
            subNavigationContent.linkTarget = '_self';
            content[section].set(url, subPageContent);
          }
        } else if (properties['enableLink']) {
          subNavigationContent.link = properties['targetUrl'];
          subNavigationContent.linkTarget = properties['new'] ? '_blank' : '_self';
        }

        for (const child of node.Children) {
          const childPageContent = this.mapContentNodes(child, section, content) as PageContent;
          if (!childPageContent) {
            continue;
          }

          const link = this.getContentLink(childPageContent);
          subNavigationContent.pages.push(link);
        }

        return subNavigationContent;
      }
      case 'CategoryBurgerLinkContent': {
        const categoryBurgerLink: CategoryBurgerLink = {
          type: 'categoryBurger',
          title: properties['titleText'],
          categoryId: properties['categoryId'],
          hideOnMobile: properties['mobileHide'],
        };

        return categoryBurgerLink;
      }
      default:
        return undefined;
    }
  }

  private setGoogleSiteVerification(type: StoreType) {
    let id;
    switch (type) {
      case StoreType.KOKKEFROEEN:
        id = 'ykxSJfG_fSCaeDE3Qu4HjgdJDjXl4f08Dc1Vb-P-D3U';
        break;
      case StoreType.NEW_YEAR:
        id = '4PqfNJdj-voGMyhFMA9BrtC8gUftjoqjdGVzqXxcR6s';
        break;
      case StoreType.FANDANKO:
        id = 'bvCPRJRnauDFzfQPeimNIjSIKPcZvryRMTXq2KKCJhM';
        break;
      case StoreType.GASTROLUX:
        id = 'dPi2nWQ6DXVtYRAZwi9_SxmbM_-xweZB5u1osSs7KfQ';
        break;
      case StoreType.LA_BRAVA:
        id = 'T0IZDo5R_Xugl3KQ8KntyLo7seL4LqMJ6Agj2a86sG8';
        break;
    }

    if (!id) {
      return;
    }

    this.metaService.addTag({ name: 'google-site-verification', content: id });
  }

  private setFacebookSiteVerification(type: StoreType) {
    let id;
    switch (type) {
      case StoreType.HERLIGHEDER:
        id = 'x7iblx2cggdwuuuvsv2elup78t0iu0';
        break;
      case StoreType.NEW_YEAR:
        id = 'dd407s57g8lp349knbmz1ydwh76jma';
        break;
    }

    if (!id) {
      return;
    }

    this.metaService.addTag({ name: 'facebook-domain-verification', content: id });
  }
}

export const contentServiceFactory = (contentService: ContentService) => {
  return () => {
    return contentService.load();
  };
};
